import { Children, cloneElement, useRef, useCallback, useState, useMemo } from "react";

import Carousel from "./Carousel";
import Slide from "./Slide";
import CarouselPagingDots from "./CarouselPagingDots";
import CarouselPlayButton from "./CarouselPlayButton";
import CarouselControlArrows from "./CarouselControlArrows";
import { useEffect } from "react";

const SlideshowCarousel = props => {
  const { slides, children } = props;
  const slidesInterval = props.slidesInterval * 1000;

  const sliderFrameRef = useRef();
  const slidesRefs = useRef([]);
  const slideRefSetter = useCallback(
    (slide, index) => (slidesRefs.current[index] = slide),
    []
  );

  const [isPaused, setIsPaused] = useState(!props.autoplay);
  const [isSlideFocused, setIsSlideFocused] = useState(false);
  const [pagingDotsDisplayed, setPagingDotsDisplayed] = useState(false);

  // The "autoplay" prop of the carousel allows controlling the carousel autoplay timer
  // enabling, so we compose its state to ensure that the carousel autoplays only if
  // the play button is not in "pause" mode, and if the contents of a slide is not
  // currently focused.
  const autoplay = !isPaused && !isSlideFocused;

  const adjustPagingDotsPosition = useCallback(
    (currentSlide, endSlide, { initial = false } = {}) => {
      // Don't adjust the paging dots position if the current slide is the same as the end
      // slide, as it's the slider initialization case, and we manually trigger the function
      // later when the DOM is ready.
      if (currentSlide === -1) return;

      // Ensure that overflowing slide indices are normalized to be contained within the
      // slides count range. There's a case where, when accessing the first slide from the
      // "next" button, the index can be the slides count, which must be 0, so the modulo
      // operator fixes that.
      const slideIndex = endSlide % slidesRefs.current.length;
      const slide = slidesRefs.current[slideIndex];

      if (!slide) return;

      const caption = slide.querySelector(".slide-caption");
      const slider = sliderFrameRef.current?.parentElement;

      // Rescue from errors if slider is not yet present
      if (!slider) return null;

      // Set the slider height to the slide content height, so that the slider can
      // accommodate the slide content height, which can vary between slides.
      const contentHeight = Array.from(slide.children).reduce(
        (acc, node) => acc + node.offsetHeight,
        0
      );
      slider.style.height = `${contentHeight}px`;

      const controls = slider.querySelector(".slider-paging-dots-list");

      if (controls && caption && caption.offsetHeight < slider.offsetHeight) {
        // Add a transition to the controls margin bottom so it transitions smoothly
        // when we change between slides.
        if (!initial) controls.style.transition = "margin-bottom 0.2s";

        controls.style.marginBottom = `${caption.offsetHeight}px`;
      } else if (controls && !caption) {
        controls.style.marginBottom = 0;
      }

      // Display the paging dots after the first slide is loaded
      if (!pagingDotsDisplayed) setPagingDotsDisplayed(true);
    },
    [pagingDotsDisplayed]
  );

  const slidesCount = slides?.length || Children.count(children);

  const slideElements = useMemo(() => {
    if (!slidesCount) return [];

    // If slides data is provided, we just map each slide to it's associated slide object,
    // which takes care of rendering it's interface
    if (slides?.length) {
      return slides.map((slide, index) => (
        <Slide
          key={slide.id}
          slide={slide}
          slideIndex={index}
          ref={slideRefSetter}
          setIsSlideFocused={setIsSlideFocused}
          // When the first slide is loaded, we adjust the paging dots position, as the
          // caption actual height can be wrong before the image is loaded.
          onLoad={() =>
            index === 0 && adjustPagingDotsPosition(null, 0, { initial: true })
          }
        />
      ));
      // If children components are provided, we clone them and provide them with additional
      // props so that they are able to interact with the SlideshowCarousel component, for
      // accessibility purposes.
    } else {
      return Children.map(children, (child, index) =>
        cloneElement(child, {
          key: index,
          slideIndex: index,
          ref: slideRefSetter,
          setIsSlideFocused
        })
      );
    }
  }, [adjustPagingDotsPosition, children, slideRefSetter, slides, slidesCount]);

  const handleKeyDown = useCallback(event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      // Pause or resume autoplay
      setIsPaused(prev => !prev);
    }
  }, []);

  useEffect(() => {
    const sliderFrame = sliderFrameRef.current;
    if (!sliderFrame) return;

    sliderFrame.addEventListener("keydown", handleKeyDown);

    return () => {
      sliderFrame.removeEventListener("keydown", handleKeyDown);
    };
  }, [autoplay, handleKeyDown]);

  if (!slidesCount) return null;

  const isCarousel = slidesCount > 1;

  return isCarousel ? (
    <div className="slideshow-carousel-container">
      <CarouselPlayButton isPaused={isPaused} setIsPaused={setIsPaused} />

      <Carousel
        autoplay={autoplay}
        autoplayInterval={slidesInterval}
        beforeSlide={adjustPagingDotsPosition}
        ref={sliderFrameRef}
        showArrows
        arrows={<CarouselControlArrows />}
        showDots
        dots={
          <CarouselPagingDots slidesRefs={slidesRefs} display={pagingDotsDisplayed} />
        }
      >
        {slideElements}
      </Carousel>
    </div>
  ) : (
    slideElements
  );
};

export default SlideshowCarousel;
